import { For, Show } from "solid-js";
import type { Accessor, Component } from "solid-js";
import { useShellStore } from "@ui/state/shell";
import { useChatProfileQuery } from "../service";
import { CollapsibleSection } from "@ui/components/CollapsibleSection";
import { InlineEditField } from "@ui/components/InlineEditField";
import { formatPhone } from "@utils/formatPhone";
import { updateUserField } from "@modules/users/service.v2";
import type { User } from "@core/services-v2";

type MaybeAccessor<T> = T | Accessor<T> | null | undefined;

const read = <T,>(input: MaybeAccessor<T>): T | undefined =>
  typeof input === "function" ? (input as Accessor<T>)() : input ?? undefined;

const labelValue = (label: string, value: string | null | undefined) => (
  <div class="detail-field">
    <span class="detail-field__label">{label}</span>
    <span class="detail-field__value">{value ?? "-"}</span>
  </div>
);

const formatDateTime = (value: Date | null | undefined) =>
  value ? value.toLocaleString() : "-";

const formatSchedule = (scheduledDate: string | null, timeSlotLabel: string | null) => {
  if (!scheduledDate && !timeSlotLabel) {
    return "Schedule pending";
  }
  if (scheduledDate && timeSlotLabel) {
    return `${scheduledDate} - ${timeSlotLabel}`;
  }
  return scheduledDate ?? timeSlotLabel ?? "Schedule pending";
};

const formatAmount = (amount: number | null, currency: string | null) => {
  if (amount === null || Number.isNaN(amount)) {
    return "-";
  }
  if (!currency) {
    return amount.toLocaleString();
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount}`;
  }
};

export const ChatProfilePanel: Component = () => {
  const { state } = useShellStore();
  const chatId = () => state.selectedChatId;
  const query = useChatProfileQuery(chatId);

  const profile = () => read(query.data) ?? null;
  const chat = () => profile()?.chat ?? null;
  const user = () => profile()?.user ?? null;
  const bookings = () => profile()?.bookings ?? [];

  const isLoading = () => Boolean(read(query.isLoading));
  const error = () => {
    const raw = read(query.error);
    if (!raw) {
      return null;
    }
    return raw instanceof Error ? raw : new Error(String(raw));
  };

  /**
   * Generic handler to update any user field
   */
  const handleUpdateUserField = async (
    fieldName: keyof Pick<User, 'displayName' | 'email' | 'phoneNumber' | 'photoURL'>,
    value: string
  ) => {
    const userId = user()?.id;
    if (!userId) {
      throw new Error("No user ID available");
    }

    await updateUserField(userId, fieldName, value);

    // Refetch the profile data to get the updated value
    query.refetch();
  };

  return (
    <div class="chat-profile-panel">
      <Show when={chatId()} fallback={<p>Select a conversation to view participant details.</p>}>
        <Show when={!isLoading()} fallback={<p>Loading chat details...</p>}>
          <Show
            when={!error()}
            fallback={
              <p class="empty-state">
                Unable to load chat details right now.
                <Show when={error()?.message}>
                  {(message) => <span class="error-detail">{message()}</span>}
                </Show>
              </p>
            }
          >
            <Show when={chat()} fallback={<p>Chat not found.</p>}>
              <CollapsibleSection title="CLIENT">
                <Show when={user()} fallback={<p class="empty-state">No registered user linked yet.</p>}>
                  {(person) => (
                    <div class="profile-contact">
                      <InlineEditField
                        value={person().displayName}
                        onSave={(value) => handleUpdateUserField('displayName', value)}
                        label="Name"
                        placeholder="Enter name..."
                      />
                      {labelValue("Email", person().email)}
                      {labelValue("Phone", formatPhone(person().phoneNumber))}
                      <Show when={person().stats}>
                        {(stats) => (
                          <div class="profile-contact__metrics">
                            <span>{stats()?.totalCleanings ?? 0} cleanings</span>
                            <Show when={stats()?.totalSquareFootCleaned}>
                              {(sqft) => <span>{sqft()?.toLocaleString()} sq ft</span>}
                            </Show>
                          </div>
                        )}
                      </Show>
                    </div>
                  )}
                </Show>
              </CollapsibleSection>

              <CollapsibleSection title="CONVERSATION">
                {labelValue("Status", chat()?.status ?? null)}
                {labelValue("Last message", chat()?.lastMessagePreview ?? null)}
                {labelValue("Last sender", chat()?.lastMessageSenderType ?? null)}
                {labelValue("Last activity", formatDateTime(chat()?.lastMessageAt))}
              </CollapsibleSection>

              <CollapsibleSection title="RECENT BOOKINGS">
                <Show when={bookings().length > 0} fallback={<p class="empty-state">No bookings recorded yet.</p>}>
                  <ul class="profile-booking-list">
                    <For each={bookings()}>
                      {(booking) => (
                        <li class="profile-booking profile-booking--condensed">
                          <div class="profile-booking__line">
                            <span class={`booking-status booking-status--${booking.status}`}>{booking.status}</span>
                            <span class="profile-booking__amount">{formatAmount(booking.amount, booking.currency)}</span>
                          </div>
                          <div class="profile-booking__line">
                            <span>{formatSchedule(booking.scheduledDate, booking.timeSlotLabel)} • {booking.roomType ?? "TBD"}</span>
                          </div>
                          <div class="profile-booking__line profile-booking__line--muted">
                            <span>{formatDateTime(booking.createdAt)}<Show when={booking.serviceId}>{(serviceId) => <> • {serviceId()}</>}</Show></span>
                          </div>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
              </CollapsibleSection>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
};

